import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { qualityManagementApi } from '../../api/qualityManagementApi';

/* ─── Renk paleti ─────────────────────────────────────────────────────────── */
const STAGE_COLORS = {
  initiation: '#3b82f6', // mavi
  action:     '#f59e0b', // sarı
  approval:   '#8b5cf6', // mor
  closing:    '#06b6d4', // camgöbeği
  completed:  '#10b981', // yeşil
  rejected:   '#ef4444', // kırmızı
};

const STAGE_LABELS = {
  initiation: 'Başlatma',
  action:     'Aksiyon',
  approval:   'Onay',
  closing:    'Kapatma',
  completed:  'Tamamlandı',
  rejected:   'Reddedildi',
};

/* ─── Yardımcı ─────────────────────────────────────────────────────────────── */
const daysBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 86_400_000);
};

const monthKey = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key) => {
  const [y, m] = key.split('-');
  const names = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  return `${names[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

/* ─── Alt bileşenler ───────────────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, color, icon: Icon, to }) => {
  const colorMap = {
    blue:   'bg-blue-50   dark:bg-blue-950/30   text-blue-600   dark:text-blue-400   border-blue-100   dark:border-blue-900/50',
    yellow: 'bg-yellow-50 dark:bg-yellow-950/30 text-yellow-600 dark:text-yellow-400 border-yellow-100 dark:border-yellow-900/50',
    purple: 'bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-900/50',
    cyan:   'bg-cyan-50   dark:bg-cyan-950/30   text-cyan-600   dark:text-cyan-400   border-cyan-100   dark:border-cyan-900/50',
    green:  'bg-green-50  dark:bg-green-950/30  text-green-600  dark:text-green-400  border-green-100  dark:border-green-900/50',
    red:    'bg-red-50    dark:bg-red-950/30    text-red-600    dark:text-red-400    border-red-100    dark:border-red-900/50',
    gray:   'bg-gray-50   dark:bg-gray-800/60   text-gray-600   dark:text-gray-400   border-gray-100   dark:border-gray-700',
  };
  const cls = colorMap[color] ?? colorMap.gray;
  const inner = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-50">{value ?? '—'}</p>
        {sub && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{sub}</p>}
      </div>
      {Icon && (
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${cls}`}>
          <Icon className="h-5 w-5" />
        </span>
      )}
    </div>
  );

  return to ? (
    <Link
      to={to}
      className="group block rounded-xl border border-gray-100 bg-white px-4 py-3.5 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
    >
      {inner}
      <p className="mt-2 flex items-center gap-1 text-[11px] text-gray-400 group-hover:text-blue-500 dark:text-gray-500">
        <span>Görüntüle</span>
        <ArrowRightIcon className="h-3 w-3" />
      </p>
    </Link>
  ) : (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {inner}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">{children}</h2>
);

const ChartWrapper = ({ title, subtitle, children, className = '' }) => (
  <div className={`rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}>
    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</p>
    {subtitle && <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800">
      {label && <p className="mb-1 font-medium text-gray-700 dark:text-gray-200">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

/* ─── Ana bileşen ──────────────────────────────────────────────────────────── */
const DoifDashboardPage = () => {
  const [forms, setForms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Tüm aşamaları tek sorguda çek
      const all = await qualityManagementApi.getForms();
      setForms(Array.isArray(all?.data) ? all.data : []);
    } catch {
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── İstatistikler ─────────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    const byStage = { initiation: 0, action: 0, approval: 0, closing: 0, completed: 0, rejected: 0 };
    const byHotel = {};
    const bySource = {};
    const byMonth = {};
    let totalDays = 0, resolvedCount = 0, overdueCount = 0;
    const today = new Date();

    forms.forEach((f) => {
      const stage = f.workflow_stage || 'initiation';
      if (stage in byStage) byStage[stage]++;

      // Otel
      const hotel = f.hotel_name || f.hotel || 'Belirsiz';
      byHotel[hotel] = (byHotel[hotel] || 0) + 1;

      // Kaynak
      const src = f.nonconformity_source || 'Belirsiz';
      bySource[src] = (bySource[src] || 0) + 1;

      // Aylık açılış
      const mk = monthKey(f.opening_date || f.created_at);
      if (mk) byMonth[mk] = (byMonth[mk] || 0) + 1;

      // Ortalama çözüm süresi
      if (stage === 'completed' && f.opening_date && f.completion_date) {
        const d = daysBetween(f.opening_date, f.completion_date);
        if (d !== null && d >= 0) { totalDays += d; resolvedCount++; }
      }

      // Süresi geçmiş
      if (!['completed', 'rejected'].includes(stage) && f.due_date) {
        if (new Date(f.due_date) < today) overdueCount++;
      }
    });

    // Son 6 ay
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({ key: k, label: monthLabel(k), value: byMonth[k] || 0 });
    }

    // Otel dağılımı (top 8)
    const hotelData = Object.entries(byHotel)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name: name.length > 18 ? name.slice(0, 16) + '…' : name, value }));

    // Kaynak dağılımı (top 6)
    const sourceData = Object.entries(bySource)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name: name.length > 20 ? name.slice(0, 18) + '…' : name, value }));

    // Aşama pie
    const stageData = Object.entries(byStage)
      .filter(([, v]) => v > 0)
      .map(([key, value]) => ({ name: STAGE_LABELS[key], value, color: STAGE_COLORS[key] }));

    const avgDays = resolvedCount > 0 ? Math.round(totalDays / resolvedCount) : null;

    return { byStage, stageData, hotelData, sourceData, months, avgDays, overdueCount, total: forms.length };
  }, [forms]);

  /* ── Son 8 form ────────────────────────────────────────────────────────── */
  const recentForms = useMemo(() =>
    [...forms].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 8),
    [forms]
  );

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (loading) return (
    <div className="flex h-64 items-center justify-center text-sm text-gray-400">
      <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…
    </div>
  );

  if (error) return (
    <div className="m-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
      {error}
    </div>
  );

  const { byStage, stageData, hotelData, sourceData, months, avgDays, overdueCount, total } = stats;

  return (
    <div className="space-y-6 px-4 py-5 sm:px-6">

      {/* ── Başlık ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-50">DOIF Dashboard</h1>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            Tüm aşamalar · {total} kayıt
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Yenile
        </button>
      </div>

      {/* ── KPI Kartlar ── */}
      <section className="space-y-2">
        <SectionTitle>Özet</SectionTitle>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          <StatCard label="Toplam" value={total} icon={ClipboardDocumentCheckIcon} color="gray" />
          <StatCard label="Başlatma" value={byStage.initiation} icon={FlagIcon} color="blue" to="/quality-management/df-baslat" />
          <StatCard label="Aksiyon" value={byStage.action} icon={ClockIcon} color="yellow" to="/quality-management/df-islemleri" />
          <StatCard label="Onay" value={byStage.approval} icon={ClipboardDocumentCheckIcon} color="purple" to="/quality-management/df-onay" />
          <StatCard label="Kapatma" value={byStage.closing} icon={ClockIcon} color="cyan" to="/quality-management/df-kapatma" />
          <StatCard label="Tamamlandı" value={byStage.completed} icon={CheckCircleIcon} color="green" />
          <StatCard label="Reddedildi" value={byStage.rejected} icon={XCircleIcon} color="red" />
        </div>
      </section>

      {/* ── İkinci kart satırı: ortalama süre + süresi geçmiş ── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Ort. Çözüm Süresi"
          value={avgDays !== null ? `${avgDays} gün` : '—'}
          sub="Tamamlanan DÖİF'ler için"
          icon={ClockIcon}
          color="gray"
        />
        <StatCard
          label="Süresi Geçmiş"
          value={overdueCount}
          sub="Termin tarihi aşılmış, açık DÖİF"
          icon={ExclamationTriangleIcon}
          color={overdueCount > 0 ? 'red' : 'green'}
        />
        <StatCard
          label="Tamamlanma Oranı"
          value={total > 0 ? `%${Math.round((byStage.completed / total) * 100)}` : '—'}
          sub={`${byStage.completed} / ${total} DÖİF`}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          label="Aktif DÖİF"
          value={byStage.initiation + byStage.action + byStage.approval + byStage.closing}
          sub="Başlatma + Aksiyon + Onay + Kapatma"
          icon={FlagIcon}
          color="blue"
        />
      </div>

      {/* ── Grafikler ── */}
      <section className="space-y-2">
        <SectionTitle>Dağılım Grafikleri</SectionTitle>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

          {/* Aşama dağılımı — Donut */}
          <ChartWrapper title="Aşamaya Göre Dağılım" subtitle="Tüm aktif ve kapalı DÖİF'ler">
            {stageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={stageData}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {stageData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    formatter={(v) => <span className="text-[11px] text-gray-600 dark:text-gray-400">{v}</span>}
                    iconSize={10}
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-12 text-center text-xs text-gray-400">Veri yok</p>
            )}
          </ChartWrapper>

          {/* Aylık açılış trendi */}
          <ChartWrapper title="Aylık DÖİF Açılışı" subtitle="Son 6 ay" className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={months} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Açılan DÖİF" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartWrapper>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

          {/* Otele göre */}
          <ChartWrapper title="Otele Göre Dağılım" subtitle="En fazla DÖİF olan 8 otel">
            {hotelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={hotelData} layout="vertical" margin={{ top: 0, right: 16, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" className="dark:stroke-gray-700" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="DÖİF Sayısı" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-12 text-center text-xs text-gray-400">Veri yok</p>
            )}
          </ChartWrapper>

          {/* Kaynağa göre */}
          <ChartWrapper title="Uygunsuzluk Kaynağına Göre" subtitle="En sık görülen 6 kaynak">
            {sourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sourceData} layout="vertical" margin={{ top: 0, right: 16, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" className="dark:stroke-gray-700" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="DÖİF Sayısı" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-12 text-center text-xs text-gray-400">Veri yok</p>
            )}
          </ChartWrapper>
        </div>
      </section>

      {/* ── Son Kayıtlar Tablosu ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <SectionTitle>Son DOIF Kayıtları</SectionTitle>
          <Link
            to="/quality-management/df-baslat"
            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400"
          >
            Tümünü gör <ArrowRightIcon className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                {['DF No', 'Otel', 'Açılış', 'Termin', 'Aşama', 'Başlatan Dept.'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
              {recentForms.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">Kayıt bulunamadı</td>
                </tr>
              ) : (
                recentForms.map((f) => {
                  const stage = f.workflow_stage || 'initiation';
                  const isOverdue = !['completed', 'rejected'].includes(stage) && f.due_date && new Date(f.due_date) < new Date();
                  return (
                    <tr key={f.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono font-medium text-gray-800 dark:text-gray-100">
                        {f.df_definition_no || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                        {f.hotel_name || '—'}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-500 dark:text-gray-400">
                        {f.opening_date || '—'}
                      </td>
                      <td className={`px-4 py-2.5 tabular-nums ${isOverdue ? 'font-semibold text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {f.due_date || '—'}
                        {isOverdue && <span className="ml-1 text-[10px]">!</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          style={{
                            backgroundColor: `${STAGE_COLORS[stage]}20`,
                            color: STAGE_COLORS[stage],
                          }}
                        >
                          {STAGE_LABELS[stage] || stage}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">
                        {f.df_initiator_department_name || '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default DoifDashboardPage;
